<app-module-header [title]="'SHUTTLE.TITLE' | transloco" [icon]="'airport_shuttle'"></app-module-header>

@if (isLoading) {
  <mat-progress-bar mode="indeterminate"></mat-progress-bar>
}

<div class="container-fluid my-2 px-2">
  <!-- Search Field -->
  <div class="search-section">
    <mat-form-field appearance="outline" class="search-field">
      <mat-label>{{ 'SHUTTLE.SEARCH' | transloco }}</mat-label>
      <input matInput (keyup)="applyFilter($event)"
        [placeholder]="'SHUTTLE.SEARCH_PLACEHOLDER' | transloco" #input>
      <mat-icon matSuffix>search</mat-icon>
    </mat-form-field>
  </div>

  <!-- Collapsible Filters -->
  <div class="filters-container">
    <div class="filters-header" (click)="toggleFilters()">
      <div class="filters-title">
        <mat-icon>filter_list</mat-icon>
        <span>{{ 'SHUTTLE.FILTERS.TITLE' | transloco }}</span>
      </div>
      <mat-icon class="expand-icon" [class.expanded]="filtersExpanded">expand_more</mat-icon>
    </div>

    <div class="filters-content" [class.expanded]="filtersExpanded">
      <form [formGroup]="orderShuttleForm">
        <div class="filters-grid">
          <!-- Date Range -->
          <div class="filter-group">
            <mat-form-field appearance="outline">
              <mat-label>{{ 'SHUTTLE.FILTERS.DATE_RANGE' | transloco }}</mat-label>
              <mat-date-range-input [formGroup]="orderShuttleForm" [rangePicker]="dateRangePicker">
                <input matStartDate formControlName="dateFrom" [placeholder]="'SHUTTLE.FILTERS.DATE_FROM' | transloco">
                <input matEndDate formControlName="dateTo" [placeholder]="'SHUTTLE.FILTERS.DATE_TO' | transloco">
              </mat-date-range-input>
              <mat-datepicker-toggle matSuffix [for]="dateRangePicker"></mat-datepicker-toggle>
              <mat-date-range-picker #dateRangePicker></mat-date-range-picker>
            </mat-form-field>
          </div>

          <!-- Cancelled Checkbox -->
          <div class="filter-group checkbox-group">
            <mat-checkbox formControlName="excludeCancelled">
              {{ 'SHUTTLE.FILTERS.EXCLUDE_CANCELLED' | transloco }}
            </mat-checkbox>
          </div>

          <!-- Filter Actions -->
          <div class="filter-actions">
            <button mat-fab color="primary" [attr.aria-label]="'SHUTTLE.ACTIONS.REFRESH' | transloco" (click)="getShuttles()"
              [matTooltip]="'SHUTTLE.ACTIONS.REFRESH' | transloco">
              <mat-icon>refresh</mat-icon>
            </button>
            <button mat-stroked-button color="primary" (click)="clearFilters()">
              <mat-icon>clear</mat-icon>
              {{ 'SHUTTLE.ACTIONS.CLEAR_FILTERS' | transloco }}
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>

  <!-- Results -->
  <div class="results-section">
    <mat-paginator [pageSizeOptions]="[100, 150, 250]" showFirstLastButtons></mat-paginator>
    <div class="overflow-auto">
      <table mat-table matSort [dataSource]="dataSource" class="results-table mat-elevation-z2">
        <ng-container matColumnDef="orderNr">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>{{ 'SHUTTLE.COLUMNS.ORDER_NUMBER' | transloco }}</th>
          <td mat-cell *matCellDef="let orderShuttle"> {{ orderShuttle.orderNr }} </td>
        </ng-container>

        <ng-container matColumnDef="firstName">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>{{ 'SHUTTLE.COLUMNS.CUSTOMER' | transloco }}</th>
          <td mat-cell *matCellDef="let orderShuttle"> {{ orderShuttle.firstName }} </td>
        </ng-container>

        <ng-container matColumnDef="cellular">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>{{ 'SHUTTLE.COLUMNS.PHONE' | transloco }}</th>
          <td mat-cell *matCellDef="let orderShuttle"> {{ orderShuttle.cellular }}</td>
        </ng-container>

        <ng-container matColumnDef="email">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>{{ 'SHUTTLE.COLUMNS.EMAIL' | transloco }}</th>
          <td mat-cell *matCellDef="let orderShuttle"> {{ orderShuttle.email }}</td>
        </ng-container>

        <ng-container matColumnDef="passengers">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>{{ 'SHUTTLE.COLUMNS.PASSENGERS' | transloco }}</th>
          <td mat-cell *matCellDef="let orderShuttle"> {{ orderShuttle.passengers }}</td>
        </ng-container>

        <ng-container matColumnDef="shuttleDate">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>{{ 'SHUTTLE.COLUMNS.SHUTTLE_DATE' | transloco }}</th>
          <td mat-cell *matCellDef="let orderShuttle"> {{ orderShuttle.shuttleDate | date: 'yyyy-MM-dd HH:mm' }}</td>
        </ng-container>

        <ng-container matColumnDef="shuttleTypeName">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>{{ 'SHUTTLE.COLUMNS.SHUTTLE_TYPE' | transloco }}</th>
          <td mat-cell *matCellDef="let orderShuttle"> {{ orderShuttle.shuttleTypeName }}</td>
        </ng-container>

        <ng-container matColumnDef="busStartDate">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>{{ 'SHUTTLE.COLUMNS.BUS_DEPARTURE' | transloco }}</th>
          <td mat-cell *matCellDef="let orderShuttle"> {{ orderShuttle.busStartDate | date: 'yyyy-MM-dd HH:mm' }}</td>
        </ng-container>

        <ng-container matColumnDef="flightNr">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>{{ 'SHUTTLE.COLUMNS.FLIGHT_NUMBER' | transloco }}</th>
          <td mat-cell *matCellDef="let orderShuttle"> {{ orderShuttle.flightNr }}</td>
        </ng-container>

        <ng-container matColumnDef="terminal">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>{{ 'SHUTTLE.COLUMNS.TERMINAL' | transloco }}</th>
          <td mat-cell *matCellDef="let orderShuttle"> {{ orderShuttle.terminal }}</td>
        </ng-container>

        <ng-container matColumnDef="busStatus">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>{{ 'SHUTTLE.COLUMNS.BUS_STATUS' | transloco }}</th>
          <td mat-cell *matCellDef="let orderShuttle"> {{ orderShuttle.busStatus }}</td>
        </ng-container>

        <ng-container matColumnDef="boardedTime">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>{{ 'SHUTTLE.COLUMNS.BOARDED_TIME' | transloco }}</th>
          <td mat-cell *matCellDef="let orderShuttle"> {{ orderShuttle.boardedTime }}</td>
        </ng-container>

        <ng-container matColumnDef="status">
          <th mat-header-cell *matHeaderCellDef mat-sort-header>{{ 'SHUTTLE.COLUMNS.STATUS' | transloco }}</th>
          <td mat-cell *matCellDef="let orderShuttle">
            @if (orderShuttle.status === 'Paid') {
              <span class="paid">{{ 'SHUTTLE.STATUS.ACTIVE' | transloco }}</span>
            }
            @if (orderShuttle.status === 'Cancelled') {
              <span class="cancelled">{{ 'SHUTTLE.STATUS.CANCELLED' | transloco }}</span>
            }
            @if (orderShuttle.status != 'Cancelled' && orderShuttle.status != 'Paid') {
              <span>{{ orderShuttle.status }}</span>
            }
          </td>
        </ng-container>

        <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
        <tr mat-row *matRowDef="let orderShuttle; columns: displayedColumns;"
          (click)="goToOrder(orderShuttle.orderId)" class="clickable-row">
        </tr>

        <tr class="mat-row" *matNoDataRow>
          <td class="mat-cell no-data" [attr.colspan]="displayedColumns.length">
            <div class="no-data-content">
              <mat-icon>search_off</mat-icon>
              <span>{{ 'SHUTTLE.NO_MATCHES' | transloco: { value: input.value } }}</span>
            </div>
          </td>
        </tr>
      </table>
    </div>
  </div>
</div>
